import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  Skeleton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WorkflowSelector from './WorkflowSelector';
import { getColourFromString } from '../../../utils/ColourNameGenerator';
import { ListItemChip } from '../../../utils/ListItemChip';
import {
  ADMIN_SCOPE,
  EDITOR_SCOPE,
  REDACTED_KEY_PREFIX_LENGTH,
  styles,
  USER_SCOPE,
} from './ApiKeyUtil';

const ApiKeyItem = ({
  apiKey,
  isPendingDeletion,
  onDeleteStart,
  onDeleteCancel,
  onDeleteConfirm,
  onUpdateWorkflows,
  allWorkflows,
  isFetchingAllWorkflows,
  isLoading,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (editDialogOpen === true) {
      setSelectedWorkflows(apiKey.workflows || []);
      setSaveError('');
    }
  }, [apiKey.workflows, editDialogOpen]);

  const hasEditorScope = apiKey.scopes.includes(EDITOR_SCOPE);

  const handleEditWorkflows = () => {
    setEditDialogOpen(true);
  };

  const handleSaveWorkflows = async () => {
    setSaving(true);
    setSaveError('');

    try {
      await onUpdateWorkflows(apiKey.id, selectedWorkflows);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update workflows:', error);
      const errorMessage = error.message || 'Failed to update workflows. Please try again.';
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedWorkflows(apiKey.workflows || []);
    setSaveError('');
    setEditDialogOpen(false);
  };

  return (
    <Box key={apiKey.id}>
      <Fade in={true} timeout={300}>
        <Box sx={styles.keyItem.container(isPendingDeletion)}>
          <Box>
            <Box sx={styles.keyItem.infoContainer}>
              <Typography variant="body2" sx={styles.keyItem.nameText}>
                {apiKey.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created: {new Date(apiKey.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            <Box sx={styles.keyItem.metaContainer}>
              <Typography variant="caption" sx={styles.keyItem.keyText}>
                {'•'.repeat(REDACTED_KEY_PREFIX_LENGTH)}
                {apiKey.redactedKey}
              </Typography>
            </Box>
            <div className="api-scope-chips-container">
              {apiKey.scopes.map(scope => (
                <Chip
                  key={scope}
                  label={scope === USER_SCOPE ? `${scope}: ${apiKey.userDisplayName}` : scope}
                  size="small"
                  color={
                    scope === ADMIN_SCOPE ? 'error' : scope === EDITOR_SCOPE ? 'warning' : 'primary'
                  }
                  variant="outlined"
                  className="api-scope-chip"
                />
              ))}
            </div>

            {hasEditorScope && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Workflows:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {isFetchingAllWorkflows || !apiKey.workflows ? (
                    <>
                      <Skeleton
                        variant="rectangular"
                        width={80}
                        height={18}
                        sx={{ borderRadius: '9px' }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={18}
                        sx={{ borderRadius: '9px' }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={70}
                        height={18}
                        sx={{ borderRadius: '9px' }}
                      />
                    </>
                  ) : apiKey.workflows.length > 0 ? (
                    // Show actual workflow chips
                    <>
                      {apiKey.workflows.slice(0, 3).map((workflow, index) => (
                        <ListItemChip
                          key={workflow.projectId || index}
                          label={workflow.projectName || workflow}
                          colour={getColourFromString(workflow.projectName || workflow)}
                          size="small"
                        />
                      ))}
                      {apiKey.workflows.length > 3 && (
                        <Chip
                          label={`+${apiKey.workflows.length - 3} more`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      No workflows assigned
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
          <Box>
            {isPendingDeletion ? (
              <Fade in={true} timeout={200}>
                <Box sx={styles.keyItem.actionsContainer}>
                  <Button variant="outlined" size="small" onClick={onDeleteCancel}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => onDeleteConfirm(apiKey.id)}
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isLoading ? 'Deleting...' : 'Delete'}
                  </Button>
                </Box>
              </Fade>
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {hasEditorScope && (
                  <IconButton size="small" onClick={handleEditWorkflows} title="Edit workflows">
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" color="error" onClick={() => onDeleteStart(apiKey)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
      <Collapse in={isPendingDeletion} timeout={300}>
        <Box sx={styles.keyItem.deleteWarning}>
          <Typography variant="caption" color="error">
            Are you sure you want to delete this API key? This action cannot be undone.
          </Typography>
        </Box>
      </Collapse>

      {/* Edit Workflows Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit Workflows for "{apiKey.name}"</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            <WorkflowSelector
              selectedWorkflows={selectedWorkflows}
              onWorkflowChange={workflows => {
                setSelectedWorkflows(workflows);
                setSaveError('');
              }}
              disabled={saving || isFetchingAllWorkflows}
              loading={isFetchingAllWorkflows}
              allWorkflows={allWorkflows}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveWorkflows}
            variant="contained"
            disabled={saving || selectedWorkflows.length === 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeyItem;
